import ContestIndividual from '../ContestIndividual.vue';
import { baseUrl } from '../../env';
import { DateTime } from 'luxon';
import axios from 'axios';
import loading from 'vue-loading-overlay';
import VueSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { Tabs, Tab } from 'vue3-tabs-component';
import { srCyrilCustom } from '../../locales/srCyrilCustom.js';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import BrainSearch from '../../components/BrainSearch.vue';
import LoadingModal from '@/components/LoadingModal.vue';
import SortArrow from '@/components/SortArrow.vue';
import PublicStatisticsYearly from '@/components/PublicStatisticsYearly.vue';

export default {
  name: 'ContestPage',

  components: {
    ContestIndividual,
    loading,
    VueSelect,
    Tabs,
    Tab,
    srCyrilCustom,
    VueDatePicker,
    BrainSearch,
    LoadingModal,
    SortArrow,
    PublicStatisticsYearly
  },

  data() {
    return {
      townships: [],
      selectedTownship: null,
      date: { from: null, to: null },
      loading: false,
      loadingLabel: 'Учитавање резултата...',
      data: [],
      languages: { srCyrilCustom },
      aiInput: '',
      aiLoading: false,
      searchTrigger: 0,
      query: '',
      headerText: 'Резултати претраге:',
      sortKey: 'calendar',
      sortAsc: false,
      searchPerformed: false,

      // activeSearch control
      activeSearch: null, // 'ai' | 'contest'
      // track whether user explicitly selected a tab (if you want to use later)
      userSelectedSearch: false,
    };
  },

  computed: {
    safeDate() {
      return this.date || { from: null, to: null };
    },

    route() {
      // defensive return - used in template
      return this.$route || {};
    },

    contests() {
      const parseAndFormat = (cal) => {
        if (!cal && cal !== 0) return null;

        // If it's already a Date object
        if (cal instanceof Date) {
          const dt = DateTime.fromJSDate(cal);
          return dt.isValid ? dt.toFormat('dd.MM.yyyy.') : null;
        }

        let s = String(cal).trim();

        // treat zero/placeholder dates as null
        if (/^(0{4}-0{2}-0{2}|0000-00-00|0{2}\.0{2}\.0{4}\.?|01\.01\.0000\.?)$/.test(s)) {
          return null;
        }

        // 1) try ISO
        let dt = DateTime.fromISO(s);
        if (dt.isValid) return dt.toFormat('dd.MM.yyyy.');

        // 2) numeric timestamps (seconds or milliseconds)
        if (/^\d+$/.test(s)) {
          const n = parseInt(s, 10);
          dt = s.length > 11 ? DateTime.fromMillis(n) : DateTime.fromSeconds(n);
          if (dt.isValid) return dt.toFormat('dd.MM.yyyy.');
        }

        // 3) try a few dd.MM.yyyy variants
        const formats = ['dd.MM.yyyy.', 'dd.MM.yyyy', 'd.M.yyyy.', 'd.M.yyyy'];
        for (const fmt of formats) {
          dt = DateTime.fromFormat(s, fmt);
          if (dt.isValid) return dt.toFormat('dd.MM.yyyy.');
        }

        // 4) last resort: SQL-like parsing
        dt = DateTime.fromSQL(s);
        if (dt.isValid) return dt.toFormat('dd.MM.yyyy.');

        return null;
      };

      return this.data.map((item) => {
        // safe parse calendar:
        const calFormatted = parseAndFormat(item.calendar);
        item.calendar = calFormatted; // sets either 'dd.MM.yyyy.' or null

        // handle contest_not_announced_date -> extract year if possible
        let notAnnYear = null;
        if (item.contest_not_announced_date) {
          const cand = String(item.contest_not_announced_date).trim();
          const dtAnn = DateTime.fromISO(cand).isValid
            ? DateTime.fromISO(cand)
            : DateTime.fromFormat(cand, 'dd.MM.yyyy.');
          if (dtAnn && dtAnn.isValid) notAnnYear = dtAnn.year;
        }

        if (item.calendar && item.calendar !== '01.01.0000.' && item.competition_published === 'Da') {
          item.title = `Расписан: ${item.calendar}`;
        } else if (notAnnYear) {
          item.title = `Није расписан ${notAnnYear}.`;
        } else {
          item.title = 'Није пасписан';
        }

        return item;
      });
    },

    sortedContests() {
      if (!this.data || !this.data.length) return [];

      return [...this.data].sort((a, b) => {
        let valA = this.getNestedValue(a, this.sortKey);
        let valB = this.getNestedValue(b, this.sortKey);

        // If sorting by calendar, parse dates
        if (this.sortKey === 'calendar') {
          valA = this.parseDate(valA);
          valB = this.parseDate(valB);
        }

        if (valA < valB) return this.sortAsc ? -1 : 1;
        if (valA > valB) return this.sortAsc ? 1 : -1;
        return 0;
      });
    },
  },

  watch: {
    // watch route changes and react
    $route(to) {
      // handle route change
      this.handleRouteChange(to);
    }
  },

  async created() {
    // Initialize from URL query or null
    this.date.from = this.$route?.query?.od ? DateTime.fromFormat(this.$route.query.od, 'dd.MM.yyyy.').toJSDate() : null;
    this.date.to = this.$route?.query?.do ? DateTime.fromFormat(this.$route.query.do, 'dd.MM.yyyy.').toJSDate() : null;

    // Prepare selectedTownship if given in query (townships loaded later in fetchData)
    if (this.$route?.query?.jedinica) {
      this.selectedTownship = { id: this.$route.query.jedinica, name: '...' };
    }

this.activeSearch =
  (this.$route?.params?.id || this.$route?.query?.jedinica)
    ? 'contest'
    : 'contest'; // default is contest

    await this.fetchData();

    // Update header text on load
    this.updateHeaderText(this.$route);
  },

  methods: {
    // Safe nested getter
    getNestedValue(obj, key) {
      if (!key) return undefined;
      return key.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
    },

    // Parse date string 'dd.MM.yyyy.' to Date for sorting
    parseDate(dateStr) {
      if (!dateStr) return new Date(0);
      const parts = String(dateStr).split('.');
      if (parts.length < 3) return new Date(0);
      const year = parseInt(parts[2], 10) || 0;
      const month = parseInt(parts[1], 10) - 1 || 0;
      const day = parseInt(parts[0], 10) || 1;
      return new Date(year, month, day);
    },

    // Switch active search tab (user clicked a tab)
    setActiveSearch(type) {
      this.activeSearch = type;
      this.userSelectedSearch = true; // user made explicit choice
      if (type === 'ai') {
        // show AI and trigger search UI (but do not change route)
        this.aiLoading = false; // ensure loading false until a search is started
      }
    },

    // Called by the AI search button to ensure AI view is visible and search triggers
    activateAiSearch() {
      // prevent any form submission or routing; ensure AI view is visible
      this.activeSearch = 'ai';
      this.userSelectedSearch = true;
      // Start AI search trigger
      this.aiLoading = true;
      this.searchTrigger++;
      // If you have asynchronous AI fetch, do it here; simulate turn-off loader after some time
      // but better to set aiLoading false when BrainSearch emits results
      setTimeout(() => {
        this.aiLoading = false;
      }, 1200);
      // Scroll to results anchor
      this.$nextTick(() => {
        const el = document.getElementById('BrainSearchResults');
        if (el) {
          const yOffset = -80;
          const targetY = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          this.smoothScrollTo(targetY, 1200);
        }
      });
    },

    // Fetch townships and contest data depending on route/query
    async fetchData() {
      this.loading = true;
      try {
        // load townships first
        const townshipsRes = await axios.get(`${baseUrl}/townships`);
        if (Array.isArray(townshipsRes.data)) {
          townshipsRes.data.unshift({ name: 'Sve jedinice', id: 'all' });
          this.townships = townshipsRes.data;
        }

        // If route has params.id (detail view)
        if (this.$route && this.$route.params && this.$route.params.id) {
          const response = await axios.get(`${baseUrl}/contest/${this.$route.params.id}`);
          this.data = response.data || [];
        } else {
          const jedinica = this.$route && this.$route.query && this.$route.query.jedinica ? this.$route.query.jedinica : null;
          const from = (this.$route && this.$route.query && this.$route.query.od) ? this.$route.query.od : '01.01.1971.';
          const to = (this.$route && this.$route.query && this.$route.query.do) ? this.$route.query.do : DateTime.local().toFormat('dd.MM.yyyy.');

          if (jedinica) {
            const response = await axios.get(`${baseUrl}/contests/${jedinica}/${from}/${to}`);
            this.data = response.data || [];
            // set selectedTownship name if empty
            if (!this.selectedTownship || this.selectedTownship.id !== jedinica) {
              const found = this.townships.find(t => String(t.id) === String(jedinica));
              this.selectedTownship = found ? { id: found.id, name: found.name } : { id: jedinica, name: '...' };
            }
          } else {
            this.data = [];
          }
        }
      } catch (error) {
        console.error('Fetch data error:', error);
      } finally {
        this.loading = false;
      }
    },

    // Form submit for contest search
    submit() {
      this.searchPerformed = true;
      const from = this.date.from ? DateTime.fromJSDate(this.date.from).toFormat('dd.MM.yyyy.') : null;
      const to = this.date.to ? DateTime.fromJSDate(this.date.to).toFormat('dd.MM.yyyy.') : null;

      const query = {
        jedinica: this.selectedTownship ? this.selectedTownship.id : null,
        od: from,
        do: to,
      };

      if (!this.selectedTownship) delete query.jedinica;
      if (!this.date.from) delete query.od;
      if (!this.date.to) delete query.do;

      // push to router with query
      if (this.$route && this.$route.params && this.$route.params.id) {
        this.$router.push({ name: 'Contest', query });
      } else {
        this.$router.push({ query });
      }

      // --- NEW: scroll to results after route update ---
        this.$nextTick(() => {
          // Wait a bit for results to render
          setTimeout(() => {
            const el = document.querySelector('.grid');
            if (el) {
              const yOffset = -80;
              const targetY = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
              this.smoothScrollTo(targetY, 800);
            }
          }, 500);
        });
    },

    // Smooth scroll helper
    smoothScrollTo(targetY, duration = 600) {
      const startY = window.pageYOffset;
      const distance = targetY - startY;
      let startTime = null;

      function animation(currentTime) {
        if (!startTime) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);

        const ease = progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;

        window.scrollTo(0, startY + distance * ease);

        if (timeElapsed < duration) {
          requestAnimationFrame(animation);
        }
      }

      requestAnimationFrame(animation);
    },

    updateHeaderText(route) {
      if (route && route.params && route.params.id) {
        this.headerText = 'Детаљи конкурса: ' + route.params.id;
      } else {
        this.headerText = 'Резултати претраге:';
      }
    },

    // Called on route change (watcher)
    async handleRouteChange(to) {
      // fetch fresh data for the new route
      await this.fetchData();

      // update header
      this.updateHeaderText(to);

      // If the route indicates a contest detail or a selected unit, always show contest view
      if ((to && to.path && to.path.includes('/konkurs/')) || (to && to.query && to.query.jedinica)) {
        this.activeSearch = 'contest';
        // reset userSelectedSearch so future explicit clicks will be considered
        this.userSelectedSearch = false;
      }

      // if a detail anchor present, scroll to detail
      this.$nextTick(() => {
        if (to && to.params && to.params.id) {
          const detailEl = this.$refs.detailSection;
          if (detailEl) {
            const rect = detailEl.getBoundingClientRect();
            const scrollY = window.pageYOffset + rect.top - 80;
            this.smoothScrollTo(scrollY);
          }
        }
      });
    },

    // Sorting helpers
    setSort(key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortKey = key;
        this.sortAsc = true;
      }
      this.sortData();
    },

    sortData() {
      this.data.sort((a, b) => {
        const valA = this.getNestedValue(a, this.sortKey);
        const valB = this.getNestedValue(b, this.sortKey);

        if (valA === valB) return 0;
        if (valA === undefined || valA === null) return 1;
        if (valB === undefined || valB === null) return -1;

        if (typeof valA === 'string' && typeof valB === 'string') {
          return this.sortAsc
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }

        // For numbers or dates
        return this.sortAsc ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
      });
    }
  }
};
